import { useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Checkbox, FormControlLabel } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";

import { TableRow } from "./DataTable";

type FilterType = 'dropdown' | 'text' | 'date' | 'boolean';

export interface FilterConfig {
  key: string;
  label: string;
  type: FilterType;
  possibleValues?: string[]; // Note: for dropdown filters only
  addAllValuesSelector?: boolean; // Note: for dropdown filters only, adds "All" option to the dropdown
  valueIndex?: number;
  placeholder?: string;
  value: string | null;
}

export const TableFiltersElement = <IDType extends number | string>({
  state,
  setState,
  data,
  disableContainer,
  maxTableWidthPixels,
  additionalTableFilterSectionContent
}: {
  state: FilterConfig[];
  setState: React.Dispatch<React.SetStateAction<FilterConfig[]>>;
  data: TableRow<IDType>[];
  disableContainer?: boolean;
  maxTableWidthPixels?: number;
  additionalTableFilterSectionContent?: React.ReactNode;
}) => {
  const [localTextInputs, setLocalTextInputs] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    state.forEach(filter => {
      if (filter.type === 'text') {
        initial[filter.key] = filter.value ?? '';
      }
    });
    return initial;
  });

  const handleFilterChange = (key: string, value: string) => {
    setState((prev: FilterConfig[]) => {
      const current = prev.find(f => f.key === key);
      if (!current || current.value === value) return prev;

      return prev.map(f =>
        f.key === key ? { ...f, value } : f
      );
    });
  };

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTextInputChange = (key: string, value: string) => {
    setLocalTextInputs(prev => ({ ...prev, [key]: value }));

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      handleFilterChange(key, value);
    }, 700);
  };

  const getDropdownOptions = (filter: FilterConfig): string[] => {
    const index = filter.valueIndex;

    return filter.possibleValues ??
      Array.from(
        new Set(
          data
            .map(row => row.values[index!]?.value)
            .filter((val): val is string => typeof val === 'string')
        )
      );
  };

  return (
    <div className={`shrink-0 overflow-y-hidden mt-4 compact:mt-2 ${disableContainer ? 'w-full' : 'container'}`}>
      <div className='bg-theme-surface-2 px-2 py-3 compact:py-1.5 border border-theme-table-heading-border mb-4 compact:mb-2 shadow'>
        <div style={{
          maxWidth: disableContainer ? maxTableWidthPixels : 'unset',
          margin: disableContainer ? '0 auto' : 'unset',
        }}>
          <div className="border-b border-theme-table-row-border pb-2 mb-4 compact:pb-1 compact:mb-2 text-theme-table-heading-contrast">
            <h3 className="font-semibold text-[1.6rem] compact:text-xl">Filters</h3>
          </div>
          <div className="flex flex-col md:flex-row">
            <div className="grid grid-cols-2 md:flex md:flex-row gap-4 compact:gap-2 items-center">
              {state?.map((filter) => {
                if (filter.type === 'dropdown' && filter.valueIndex !== undefined) {
                  const options = getDropdownOptions(filter);
                  return (
                    <div className="w-full h-full" key={filter.key}>
                      <select
                        className="w-full h-full border border-theme-border rounded-md p-2 bg-theme-surface-2 hover:bg-theme-surface transition ease-in-out text-theme-table-heading-contrast"
                        value={filter.value ?? ''}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      >
                        {filter.addAllValuesSelector && <option value=''>All {filter.label}</option>}
                        {options.map((optionValue, index) => (
                          <option key={index} value={optionValue}>
                            {optionValue}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }

                if (filter.type === 'text') {
                  return (
                    <div className="w-full" key={filter.key}>
                      <TextField
                        id={filter.key}
                        label={filter.placeholder ?? ''}
                        size="small"
                        classes={{ root: 'w-full' }}
                        value={localTextInputs[filter.key] ?? ''}
                        onChange={(e) => handleTextInputChange(filter.key, e.target.value)}
                      />
                    </div>
                  );
                }

                if (filter.type === 'date') {
                  return (
                    <div className="w-full" key={filter.key}>
                      <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePicker
                          className="w-full"
                          slotProps={{
                            textField: {
                              size: "small",
                            },
                            field: {
                              clearable: true
                            }
                          }}
                          label={filter.label}
                          value={filter.value ? DateTime.fromISO(filter.value) : null}
                          onChange={(value) => handleFilterChange(filter.key, (value as DateTime)?.toISODate() ?? "")}
                        />
                      </LocalizationProvider>
                    </div>
                  );
                }

                if (filter.type === 'boolean') {
                  return (
                    <div className="pt-2 w-full" key={filter.key}>
                      <FormControlLabel
                        id={filter.key}
                        control={
                          <Checkbox
                            classes={{ root: 'w-auto' }}
                            checked={filter.value === 'true'}
                            onChange={e => handleFilterChange(filter.key, e.target.checked ? 'true' : 'false')}
                            slotProps={{
                              input: { "aria-label": filter.placeholder },
                            }}
                            className="!text-theme-text"
                          />
                        }
                        slotProps={{
                          typography: {
                            className: '!text-theme-text',
                          },
                        }}
                        label={filter.placeholder ?? ''}
                        labelPlacement="top"
                      />
                    </div>
                  );
                }

                return null;
              })}
            </div>
          </div>
          {additionalTableFilterSectionContent && <div className="mt-4">{additionalTableFilterSectionContent}</div>}
        </div>
      </div>
    </div>
  );
};